import type { CategoryMenuItem } from '~/types/category'

/**
 * Titles and meta descriptions (SEO review 22.10.2026):
 * - categories: 54 % had no description, 39 % one over 160 characters, and 391 pages shared a title with
 *   another category of the same name (e.g. 12× "18" vanteelle")
 * - products: 9 % had no description and 44 % one under 70 characters
 */

/** Google shows about 155–160 characters of a description */
export const DESCRIPTION_MAX = 155

const PICKUP = 'Tilaa verkosta Tarvike.comista tai nouda myymälöistämme Oulusta, Ranualta ja Rovaniemeltä.'
const PICKUP_SHORT = 'Tilaa verkosta Tarvike.comista.'

/** The intro with the longest closing line that fits, so the text is never cut in the middle */
const withClosing = (intro: string) =>
  [`${intro} ${PICKUP}`, `${intro} ${PICKUP_SHORT}`].find(text => text.length <= DESCRIPTION_MAX) || truncateText(intro)

/** Plain text of HTML: tags and entities out, whitespace collapsed */
export function plainText(html?: string | null): string {
  return (html || '')
    // Entities first: some descriptions have their tags as &lt;span&gt;, which must not end up as text
    .replace(/&nbsp;/g, ' ').replace(/&quot;/g, '"').replace(/&#39;/g, '\'').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    // The end of a tag cut off at the start of the text, e.g. 'mso-ansi-language:EN-GB">Primary clutch…' (22.10.2026)
    .replace(/^[^<>\s]*>/, '')
    .replace(/<\/(p|li|h\d|div)>/gi, '. ')
    // Quote-aware: Word HTML from Struct can have a tag inside an attribute, style="…;<p>mso-ansi-language:EN-GB"
    .replace(/<\/?[a-z!][^>"']*(?:(?:"[^"]*"|'[^']*')[^>"']*)*>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/(\.\s*){2,}/g, '. ')
    .replace(/\s+([.,!?])/g, '$1')
    .trim()
    .replace(/^[.\s]+/, '')
}

/** Whole sentences up to the limit, or cut at a word boundary with "…" */
export function truncateText(text: string, max = DESCRIPTION_MAX): string {
  if (text.length <= max) return text
  // A sentence ends at punctuation followed by a space: "Tarvike.com" is not two sentences
  const sentences = text.split(/(?<=[.!?])\s+/)
  let result = ''
  for (const sentence of sentences) {
    if ((result ? `${result} ${sentence}` : sentence).length > max) break
    result = result ? `${result} ${sentence}` : sentence
  }
  if (result.length >= max * 0.6 && /[.!?]$/.test(result)) return result
  const cut = text.slice(0, max - 1)
  return `${cut.slice(0, Math.max(cut.lastIndexOf(' '), max * 0.6)).replace(/[\s,;:–-]+$/, '')}…`
}

const parentOf = (category: CategoryMenuItem, categories: CategoryMenuItem[]) =>
  category.parentId ? categories.find(c => c.id === category.parentId) : undefined

const ancestors = (category: CategoryMenuItem, categories: CategoryMenuItem[]) => {
  const result: CategoryMenuItem[] = []
  for (let parent = parentOf(category, categories); parent && result.length < 10; parent = parentOf(parent, categories)) result.push(parent)
  return result
}

/**
 * "{name}" when the name is unique, otherwise "{name} – {ancestor}" with the nearest ancestor that tells the
 * same-named categories apart: "Virtalukot – Mönkijöiden varaosat", not "Virtalukot – Sähköosat" three times.
 */
export function categoryPageTitle(category: CategoryMenuItem, categories: CategoryMenuItem[]): string {
  const sameName = categories.filter(c => c.name === category.name && c.isActive !== false)
  if (sameName.length < 2) return category.name
  const own = ancestors(category, categories)
  const others = sameName.filter(c => c.id !== category.id).map(c => ancestors(c, categories))
  // The nearest ancestor whose name no other same-named category has at the same distance
  const level = own.findIndex((ancestor, i) => others.every(o => o[i]?.name !== ancestor.name))
  const label = own[level >= 0 ? level : 0]
  return label ? `${category.name} – ${label.name}` : category.name
}

/**
 * The category's own description (whole sentences, at most 155 characters); without one, the subcategories
 * ("Renkaat: Crossi-renkaat, Enduro-renkaat ja muut.") or the parent, plus the order and pickup line.
 */
export function categoryMetaDescription(category: CategoryMenuItem, categories: CategoryMenuItem[], descriptionHtml?: string | null): string {
  const own = plainText(descriptionHtml)
  if (own.length >= 50) return truncateText(own)

  const children = categories
    .filter(c => c.parentId === category.id && c.isActive !== false && c.isIncludedInMenu !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map(c => c.name)
  const parent = parentOf(category, categories)
  let intro: string
  if (children.length) {
    const shown = children.slice(0, 4)
    intro = `${category.name}: ${shown.join(', ')}${children.length > shown.length ? ' ja muut' : ''}.`
  }
  else {
    intro = parent ? `${category.name} – ${parent.name}.` : `${category.name}.`
  }
  return withClosing(intro)
}

/**
 * The product's short description; when it is missing or very short (e.g. "Varaosat ja lisävarusteet."),
 * name, brand, price and stock in front of it.
 */
export function productMetaDescription(product: { name: string, brand?: string, price: number, stockStatus?: string, shortDescriptionHtml?: string | null }): string {
  const own = plainText(product.shortDescriptionHtml)
  if (own.length >= 70) return truncateText(own)

  const name = product.brand && !product.name.toLowerCase().includes(product.brand.toLowerCase())
    ? `${product.brand} ${product.name}`
    : product.name
  const price = `${product.price.toFixed(2).replace('.', ',')} €`
  const stock = { inStock: ', varastossa', backorder: ', tilattavissa' }[product.stockStatus || ''] || ''
  return withClosing(`${name}. ${own ? `${own.replace(/\.?$/, '.')} ` : ''}Hinta ${price}${stock}.`)
}
